// ============================================================================
// Action Requirements - Generic types for action prerequisites
// ============================================================================
// ============================================================================
// Requirement Type Constants
// ============================================================================
export const RequirementType = {
    HAS_PAWN_SELECTED: 'has_pawn_selected',
    EQUIPPED_ITEM: 'equipped_item',
    SKILL_LEVEL: 'skill_level',
    ITEM_TIER: 'item_tier',
    HAS_ITEM: 'has_item',
};
// ============================================================================
// Requirement Creation Helpers
// ============================================================================
export const createHasPawnSelectedRequirement = () => ({
    type: RequirementType.HAS_PAWN_SELECTED,
    id: 'pawn_selected',
});
export const createEquippedItemRequirement = ({ itemCategory, }) => ({
    type: RequirementType.EQUIPPED_ITEM,
    id: `equipped_${itemCategory}`,
    itemCategory,
});
export const createSkillLevelRequirement = ({ skillId, minLevel, }) => ({
    type: RequirementType.SKILL_LEVEL,
    id: `skill_${skillId}`,
    skillId,
    minLevel,
});
export const createItemTierRequirement = ({ itemCategory, minTier, targetName, }) => ({
    type: RequirementType.ITEM_TIER,
    id: `tier_${itemCategory}`,
    itemCategory,
    minTier,
    targetName,
});
export const createHasItemRequirement = ({ itemType, quantity, }) => ({
    type: RequirementType.HAS_ITEM,
    id: `has_item_${itemType}`,
    itemType,
    quantity,
});
// ============================================================================
// Validation Functions
// ============================================================================
const validateHasPawnSelected = ({ context, }) => ({
    requirement: { type: RequirementType.HAS_PAWN_SELECTED, id: 'pawn_selected' },
    met: context.hasPawnSelected,
    currentValue: context.selectedPawnCount,
    requiredValue: 1,
});
const validateEquippedItem = ({ requirement, context, }) => {
    let hasItem = false;
    switch (requirement.itemCategory) {
        case 'pickaxe':
            hasItem = context.equippedPickaxe !== null;
            break;
        case 'axe':
            hasItem = context.equippedAxe !== null;
            break;
        case 'weapon':
            hasItem = context.equippedWeapon !== null;
            break;
        case 'fishing_rod':
            hasItem = context.equippedFishingRod !== null;
            break;
    }
    return {
        requirement,
        met: hasItem,
        currentValue: hasItem,
        requiredValue: true,
    };
};
const validateSkillLevel = ({ requirement, context, }) => {
    let currentLevel = 0;
    switch (requirement.skillId) {
        case 'mining':
            currentLevel = context.miningLevel;
            break;
        case 'woodcutting':
            currentLevel = context.woodcuttingLevel;
            break;
        case 'fishing':
            currentLevel = context.fishingLevel;
            break;
        default:
            currentLevel = 0;
    }
    return {
        requirement,
        met: currentLevel >= requirement.minLevel,
        currentValue: currentLevel,
        requiredValue: requirement.minLevel,
    };
};
const validateItemTier = ({ requirement, context, }) => {
    let currentTier = 0;
    switch (requirement.itemCategory) {
        case 'pickaxe':
            currentTier = context.equippedPickaxeTier;
            break;
        case 'axe':
            currentTier = context.equippedAxeTier;
            break;
    }
    return {
        requirement,
        met: currentTier >= requirement.minTier,
        currentValue: currentTier,
        requiredValue: requirement.minTier,
    };
};
const validateHasItem = ({ requirement, context, }) => {
    const item = context.inventory.find((i) => i.itemType === requirement.itemType);
    const currentQuantity = item?.quantity ?? 0;
    return {
        requirement,
        met: currentQuantity >= requirement.quantity,
        currentValue: currentQuantity,
        requiredValue: requirement.quantity,
    };
};
/** Validate a single requirement against context */
export const validateRequirement = ({ requirement, context, }) => {
    switch (requirement.type) {
        case RequirementType.HAS_PAWN_SELECTED:
            return validateHasPawnSelected({ requirement, context });
        case RequirementType.EQUIPPED_ITEM:
            return validateEquippedItem({ requirement, context });
        case RequirementType.SKILL_LEVEL:
            return validateSkillLevel({ requirement, context });
        case RequirementType.ITEM_TIER:
            return validateItemTier({ requirement, context });
        case RequirementType.HAS_ITEM:
            return validateHasItem({ requirement, context });
    }
};
/** Validate all requirements and return aggregated result */
export const validateRequirements = ({ requirements, context, }) => {
    const results = [];
    for (const requirement of requirements) {
        results.push(validateRequirement({ requirement, context }));
    }
    const unmetRequirements = results.filter((r) => !r.met);
    return {
        canPerform: unmetRequirements.length === 0,
        unmetRequirements,
        allRequirements: results,
    };
};
//# sourceMappingURL=requirements.js.map